import { Box, Button, ButtonBase, Paper, Stack, Typography, alpha, useTheme } from '@mui/material'
import CheckCircle from '@mui/icons-material/CheckCircle'
import CircleOutlined from '@mui/icons-material/CircleOutlined'
import { Fragment, FunctionComponent } from 'react'
import { IGetStartedStep } from '../../../hooks/use-get-started-steps'
import { BRAND_DISPLAY_FONT } from '../../pages/signup-login/auth/auth-branding'

// The dashboard's persistent setup guide for admins. Steps and their done state
// come from useGetStartedSteps (real state, re-derived each load); this
// component only renders them. "Hide this" is handled by the parent so the
// dismissal persists alongside the rest of the guide state.
//
// Implements the chosen "Guided panel" direction: one outlined white card with
// a slim tinted header strip (welcome, promise, mini progress bar, Hide this)
// over the five tasks as hairline-separated list rows. Settled rows are quiet
// links with filled green ticks; every outstanding row is gently tinted and
// carries its own description and contained action (Skip stays invite-only).
// The tints derive from the theme's secondary token so partner theming flows.
export const GetStartedCard: FunctionComponent<{
  steps: IGetStartedStep[];
  firstName?: string;
  onStepAction: (step: IGetStartedStep) => void;
  onSkipInvite: () => void;
  onHide: () => void;
}> = ({ steps, firstName, onStepAction, onSkipInvite, onHide }) => {
  const theme = useTheme()
  const doneCount = steps.filter(step => step.done || step.skipped).length

  // The mock's greens, expressed as strengths of the pill-green token: the
  // header strip at 25%, its underline at 50%, the outstanding rows at 8%.
  const headTint = alpha(theme.palette.secondary.main, 0.25)
  const headBorder = alpha(theme.palette.secondary.main, 0.5)
  const rowTint = alpha(theme.palette.secondary.main, 0.08)

  // The theme's text-Button variant paints a white plate, which would read as
  // a pill on the tinted surfaces, so the quiet actions go transparent grey.
  const quietButtonSx = {
    backgroundColor: 'transparent',
    color: 'text.secondary',
    '&:hover': { backgroundColor: 'transparent', color: 'text.primary' },
  }

  const rowDividerSx = (index: number) => index > 0
    ? { borderTop: '1px solid', borderColor: 'grey.200' }
    : {}

  // Settled (done or skipped): a quiet full-width row, still a link to where
  // the work lives.
  const settledRow = (step: IGetStartedStep, index: number) => (
    <ButtonBase
      disableRipple
      onClick={() => onStepAction(step)}
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'flex-start',
        gap: '13px',
        px: 3,
        height: 46,
        ...rowDividerSx(index),
        '&:hover': { bgcolor: 'action.hover' },
      }}>
      {step.done
        ? <CheckCircle sx={{ fontSize: 18, color: 'primary.main' }} />
        : <CircleOutlined sx={{ fontSize: 18, color: 'grey.400' }} />}
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        {step.label}
      </Typography>
    </ButtonBase>
  )

  // Outstanding: a tinted row carrying the task's description and its own
  // contained action; below sm the actions wrap under the description.
  const outstandingRow = (step: IGetStartedStep, index: number) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        columnGap: '13px',
        rowGap: 1,
        px: 3,
        py: '14px',
        bgcolor: rowTint,
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
        ...rowDividerSx(index),
      }}>
      <CircleOutlined sx={{ fontSize: 18, color: 'grey.400' }} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: BRAND_DISPLAY_FONT, fontSize: 14.5, color: 'text.primary' }}>
          {step.label}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: '2px' }}>
          {step.description}
        </Typography>
      </Box>
      <Stack
        direction='row'
        alignItems='center'
        spacing={2}
        sx={{ ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' }, pl: { xs: '31px', sm: 0 }, flexShrink: 0 }}>
        {step.id === 'invite-colleague' &&
          <Button variant='text' size='small' onClick={onSkipInvite} sx={{ ...quietButtonSx, fontSize: 13 }}>
            Skip
          </Button>}
        <Button variant='contained' size='small' onClick={() => onStepAction(step)}>
          {step.buttonLabel}
        </Button>
      </Stack>
    </Box>
  )

  return (
    <Paper variant='outlined' sx={{ mb: 2, overflow: 'hidden' }}>
      <Stack
        direction='row'
        alignItems='center'
        spacing={1.5}
        sx={{ px: 3, py: '13px', bgcolor: headTint, borderBottom: '1px solid', borderColor: headBorder, flexWrap: 'wrap' }}>
        {/* The two texts share a baseline with each other, but the pair is centred in the
            strip: the Hide button makes the row taller than the text, and baseline-aligned
            flex items would otherwise sit at the top of that line rather than centred. */}
        <Stack direction='row' alignItems='baseline' spacing={1.5} sx={{ flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: BRAND_DISPLAY_FONT, fontSize: 15.5, color: 'text.primary' }}>
            {firstName ? `Welcome, ${firstName}` : 'Welcome'}
          </Typography>
          {/* Says what the card is. It must not promise an outcome (a live footprint) that
              several of these tasks do not produce and that is often already true by the
              time the card is on screen. */}
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Finish setting up your account.
          </Typography>
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Stack direction='row' alignItems='center' spacing={1} sx={{ alignSelf: 'center' }}>
          <Box sx={{ width: 72, height: 4, borderRadius: '2px', bgcolor: 'grey.200', overflow: 'hidden' }}>
            <Box sx={{ width: `${(doneCount / steps.length) * 100}%`, height: '100%', borderRadius: '2px', bgcolor: 'primary.main' }} />
          </Box>
          <Typography sx={{ fontSize: 12.5, fontFamily: BRAND_DISPLAY_FONT, color: 'primary.dark', whiteSpace: 'nowrap' }}>
            {doneCount} of {steps.length} done
          </Typography>
        </Stack>
        <Button variant='text' size='small' onClick={onHide} sx={{ ...quietButtonSx, fontSize: 12.5, alignSelf: 'center', flexShrink: 0 }}>
          Hide this
        </Button>
      </Stack>
      {steps.map((step, index) => (
        <Fragment key={step.id}>
          {(step.done || step.skipped) ? settledRow(step, index) : outstandingRow(step, index)}
        </Fragment>
      ))}
    </Paper>
  )
}
